import { Alert, Box } from '@shared/ui';
import type React from 'react';
import { useMemo } from 'react';
import CalendarAccountSection from './CalendarAccountSection';
import type {
  CalendarAccount,
  CalendarProviderGroup,
  CalendarSelectionErrors,
  CalendarSelectionOverrides,
} from './types';

interface CalendarProviderSectionProps {
  provider: CalendarProviderGroup;
  mutationPending?: boolean;
  selectionOverrides: CalendarSelectionOverrides;
  selectionErrors: CalendarSelectionErrors;
  onReconnect: (provider: string, email?: string) => void;
  onDisconnectAccount: (args: {
    provider: string;
    connectionId: string;
    accountLabel: string;
  }) => void;
  onToggleCalendar: (args: {
    provider: string;
    connectionId: string;
    providerCalendarId: string;
    selected: boolean;
    previousSelected: boolean;
  }) => void;
}

function accountSortLabel(account: CalendarAccount): string {
  return account.email || account.name || account.provider_user_id || '';
}

export function sortMentoAccountsFirst(accounts: CalendarAccount[]): CalendarAccount[] {
  return [...accounts].sort((left, right) => {
    const leftLabel = accountSortLabel(left).toLowerCase();
    const rightLabel = accountSortLabel(right).toLowerCase();
    const leftIsMento = leftLabel.includes('@mento.co');
    const rightIsMento = rightLabel.includes('@mento.co');

    if (leftIsMento !== rightIsMento) {
      return leftIsMento ? -1 : 1;
    }

    return leftLabel.localeCompare(rightLabel);
  });
}

const CalendarProviderSection: React.FC<CalendarProviderSectionProps> = ({
  provider,
  mutationPending = false,
  selectionOverrides,
  selectionErrors,
  onReconnect,
  onDisconnectAccount,
  onToggleCalendar,
}) => {
  const sortedAccounts = useMemo(
    () => sortMentoAccountsFirst(provider.accounts),
    [provider.accounts]
  );

  return (
    <Box data-testid="calendar-provider-section" sx={{ display: 'flex', flexDirection: 'column' }}>
      {provider.error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {provider.error.message}
        </Alert>
      ) : null}

      {sortedAccounts.map((account) => (
        <CalendarAccountSection
          key={account.connection_id}
          account={account}
          provider={provider}
          mutationPending={mutationPending}
          selectionOverrides={selectionOverrides}
          selectionErrors={selectionErrors}
          onReconnect={onReconnect}
          onDisconnectAccount={onDisconnectAccount}
          onToggleCalendar={onToggleCalendar}
        />
      ))}
    </Box>
  );
};

export default CalendarProviderSection;
